import type { JSONSchema7, JSONSchema7Definition } from "json-schema";

export type JsEditorRuntimeContext =
  | "dynamicValueExpression"
  | "playerEvent"
  | "interactiveExpression"
  | "interactiveConcat"
  | "interactiveForm"
  | "embedElement";

export type JsEditorCompletionKind =
  | "property"
  | "method"
  | "enum"
  | "variable";

export type JsEditorRuntimeCompletion = {
  label: string;
  insertText: string;
  detail?: string;
  documentation?: string;
  kind: JsEditorCompletionKind;
  replacePreviousDot?: boolean;
};

type RuntimeVariableKind =
  | "schema"
  | "record"
  | "player"
  | "event"
  | "element"
  | "foreignObject"
  | "jumpTypes"
  | "primitive";

type RuntimeVariable = {
  name: string;
  kind: RuntimeVariableKind;
  schema?: JSONSchema7;
  properties?: JsEditorRuntimeCompletion[];
  detail?: string;
  documentation?: string;
};

export type JsEditorRuntimeCompletionContext = {
  runtimeContext?: JsEditorRuntimeContext;
  variables: RuntimeVariable[];
};

type BuildRuntimeContextOptions = {
  runtimeContext?: JsEditorRuntimeContext;
  perVideoSchema?: JSONSchema7 | string | null;
  liveControlSchema?: JSONSchema7 | string | null;
  inputNames?: string[];
  isControlDeprecated?: boolean;
};

type MemberExpressionInfo = {
  root: string;
  path: string[];
  prefix: string;
  hasMemberAccess: boolean;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isJsonSchemaObject = (
  value: JSONSchema7Definition | undefined
): value is JSONSchema7 => isObject(value);

const SNIPPET_MARKER = String.fromCharCode(36);
const snippetPlaceholder = (index: number, value: string) =>
  `${SNIPPET_MARKER}{${index}:${value}}`;

const cloneSchema = (schema: JSONSchema7): JSONSchema7 =>
  JSON.parse(JSON.stringify(schema));

export const parseJsonSchemaForEditor = (
  schema: JSONSchema7 | string | null | undefined
): JSONSchema7 => {
  if (!schema) return {};
  if (typeof schema !== "string") return cloneSchema(schema);

  try {
    return JSON.parse(schema || "{}");
  } catch {
    return {};
  }
};

const PLAYER_COMPLETIONS: JsEditorRuntimeCompletion[] = [
  {
    label: "play",
    insertText: "play()",
    detail: "player.play(replay?: boolean): Promise<void>",
    documentation: "Start playback.",
    kind: "method",
  },
  {
    label: "pause",
    insertText: "pause()",
    detail: "player.pause(): Promise<void>",
    documentation: "Pause playback.",
    kind: "method",
  },
  {
    label: "togglePlayPause",
    insertText: "togglePlayPause()",
    detail: "player.togglePlayPause(forcePlay?: boolean): Promise<void>",
    kind: "method",
  },
  {
    label: "stop",
    insertText: "stop()",
    detail: "player.stop(): void",
    kind: "method",
  },
  {
    label: "replay",
    insertText: "replay()",
    detail: "player.replay(): Promise<void>",
    kind: "method",
  },
  {
    label: "seekTo",
    insertText: `seekTo(${snippetPlaceholder(1, "frame")}, ${snippetPlaceholder(
      2,
      "true"
    )})`,
    detail: "player.seekTo(frame?: number, play?: boolean): Promise<void>",
    kind: "method",
  },
  {
    label: "seekToMarker",
    insertText: `seekToMarker(${snippetPlaceholder(
      1,
      "markerName"
    )}, ${snippetPlaceholder(2, "true")})`,
    detail:
      "player.seekToMarker(markerName?: string, forcePlay?: boolean): void",
    kind: "method",
  },
  {
    label: "slideTo",
    insertText: `slideTo(${snippetPlaceholder(1, "frame")})`,
    detail: "player.slideTo(frame?: number): Promise<void>",
    kind: "method",
  },
  {
    label: "getTotalFrames",
    insertText: "getTotalFrames()",
    detail: "player.getTotalFrames(): number",
    kind: "method",
  },
  {
    label: "getFrameFromTime",
    insertText: `getFrameFromTime(${snippetPlaceholder(1, "time")})`,
    detail: "player.getFrameFromTime(time: number): number",
    kind: "method",
  },
  {
    label: "getTimeFromFrame",
    insertText: `getTimeFromFrame(${snippetPlaceholder(1, "frame")})`,
    detail: "player.getTimeFromFrame(frame: number): number",
    kind: "method",
  },
  {
    label: "getInputFieldsValues",
    insertText: "getInputFieldsValues()",
    detail: "player.getInputFieldsValues(): Record<string, string>",
    kind: "method",
  },
  {
    label: "mute",
    insertText: "mute()",
    detail: "player.mute(): void",
    kind: "method",
  },
  {
    label: "unmute",
    insertText: "unmute()",
    detail: "player.unmute(): void",
    kind: "method",
  },
  {
    label: "toggleMute",
    insertText: "toggleMute()",
    detail: "player.toggleMute(): void",
    kind: "method",
  },
  {
    label: "toggleFullScreen",
    insertText: "toggleFullScreen()",
    detail: "player.toggleFullScreen(): Promise<void>",
    kind: "method",
  },
  {
    label: "update",
    insertText: `update(${snippetPlaceholder(
      1,
      "newAnimation"
    )}, ${snippetPlaceholder(2, "updates")})`,
    detail: "player.update(newAnimation, updates, goToFrame?: number): void",
    kind: "method",
  },
  {
    label: "dynamicUpdate",
    insertText: `dynamicUpdate(${snippetPlaceholder(1, "changes")})`,
    detail:
      "player.dynamicUpdate(changes, goToFrame?, options?): Promise<void>",
    kind: "method",
  },
  {
    label: "downloadFrame",
    insertText: "downloadFrame()",
    detail: "player.downloadFrame(options?): Promise<File | undefined>",
    kind: "method",
  },
  {
    label: "downloadVideo",
    insertText: "downloadVideo()",
    detail: "player.downloadVideo(options?): Promise<void>",
    kind: "method",
  },
  {
    label: "destroy",
    insertText: "destroy()",
    detail: "player.destroy(): void",
    kind: "method",
  },
  {
    label: "EE",
    insertText: "EE",
    detail: "EventEmitter<PlayerEvents>",
    documentation: "Player event emitter.",
    kind: "property",
  },
  {
    label: "animation",
    insertText: "animation",
    detail: "IAnimationItem",
    kind: "property",
  },
  {
    label: "animationData",
    insertText: "animationData",
    detail: "Animation",
    kind: "property",
  },
  {
    label: "videoData",
    insertText: "videoData",
    detail: "IJSONObject | undefined",
    kind: "property",
  },
  {
    label: "liveControlData",
    insertText: "liveControlData",
    detail: "IJSONObject | undefined",
    kind: "property",
  },
  {
    label: "videoParts",
    insertText: "videoParts",
    detail: "Array<IVideoPartFilled | IVideoPart> | undefined",
    kind: "property",
  },
  {
    label: "sceneConfig",
    insertText: "sceneConfig",
    detail: "ISceneConfig",
    kind: "property",
  },
  {
    label: "identifier",
    insertText: "identifier",
    detail: "IIdentifier | undefined",
    kind: "property",
  },
  {
    label: "muted",
    insertText: "muted",
    detail: "boolean",
    kind: "property",
  },
  {
    label: "playing",
    insertText: "playing",
    detail: "boolean",
    kind: "property",
  },
  {
    label: "pausedByUser",
    insertText: "pausedByUser",
    detail: "boolean",
    kind: "property",
  },
  {
    label: "dynamicDeep",
    insertText: "dynamicDeep",
    detail: "number",
    kind: "property",
  },
  {
    label: "mainContainer",
    insertText: "mainContainer",
    detail: "HTMLElement",
    kind: "property",
  },
  {
    label: "playerElement",
    insertText: "playerElement",
    detail: "Element",
    kind: "property",
  },
  {
    label: "mainAnimationElement",
    insertText: "mainAnimationElement",
    detail: "Element",
    kind: "property",
  },
];

const EVENT_COMPLETIONS: JsEditorRuntimeCompletion[] = [
  {
    label: "target",
    insertText: "target",
    detail: "EventTarget | null",
    kind: "property",
  },
  {
    label: "currentTarget",
    insertText: "currentTarget",
    detail: "EventTarget | null",
    kind: "property",
  },
  {
    label: "type",
    insertText: "type",
    detail: "string",
    kind: "property",
  },
  {
    label: "preventDefault",
    insertText: "preventDefault()",
    detail: "event.preventDefault(): void",
    kind: "method",
  },
  {
    label: "stopPropagation",
    insertText: "stopPropagation()",
    detail: "event.stopPropagation(): void",
    kind: "method",
  },
];

const ELEMENT_COMPLETIONS: JsEditorRuntimeCompletion[] = [
  {
    label: "classList",
    insertText: "classList",
    detail: "DOMTokenList",
    kind: "property",
  },
  {
    label: "dataset",
    insertText: "dataset",
    detail: "DOMStringMap",
    kind: "property",
  },
  {
    label: "style",
    insertText: "style",
    detail: "CSSStyleDeclaration",
    kind: "property",
  },
  {
    label: "querySelector",
    insertText: `querySelector(${snippetPlaceholder(1, "selector")})`,
    detail: "element.querySelector(selector: string): Element | null",
    kind: "method",
  },
  {
    label: "addEventListener",
    insertText: `addEventListener(${snippetPlaceholder(
      1,
      "type"
    )}, ${snippetPlaceholder(2, "listener")})`,
    detail: "element.addEventListener(type: string, listener): void",
    kind: "method",
  },
  {
    label: "setAttribute",
    insertText: `setAttribute(${snippetPlaceholder(
      1,
      "name"
    )}, ${snippetPlaceholder(2, "value")})`,
    detail: "element.setAttribute(name: string, value: string): void",
    kind: "method",
  },
];

const FOREIGN_OBJECT_COMPLETIONS: JsEditorRuntimeCompletion[] = [
  ...ELEMENT_COMPLETIONS,
  {
    label: "width",
    insertText: "width",
    detail: "SVGAnimatedLength",
    kind: "property",
  },
  {
    label: "height",
    insertText: "height",
    detail: "SVGAnimatedLength",
    kind: "property",
  },
  {
    label: "x",
    insertText: "x",
    detail: "SVGAnimatedLength",
    kind: "property",
  },
  {
    label: "y",
    insertText: "y",
    detail: "SVGAnimatedLength",
    kind: "property",
  },
];

const JUMP_TYPE_COMPLETIONS: JsEditorRuntimeCompletion[] = [
  "sceneStart",
  "sceneEnd",
  "frame",
  "currentFrame",
].map((label) => ({
  label,
  insertText: label,
  detail: `jumpTypes.${label}`,
  kind: "enum" as const,
}));

const buildInputCompletions = (
  inputNames: string[] = []
): JsEditorRuntimeCompletion[] =>
  Array.from(new Set(inputNames.filter(Boolean))).map((inputName) => ({
    label: inputName,
    insertText: getPropertyInsertText(inputName),
    replacePreviousDot: !isValidIdentifier(inputName),
    detail: "string",
    kind: "property",
  }));

const dataVariable = (schema: JSONSchema7): RuntimeVariable => ({
  name: "data",
  kind: "schema",
  schema,
  detail: "Per-video data",
  documentation: "Per-video data derived from the project data schema.",
});

const schemaVariable = (
  name: string,
  schema: JSONSchema7,
  detail: string,
  documentation: string
): RuntimeVariable => ({
  name,
  kind: "schema",
  schema,
  detail,
  documentation,
});

const inputVariable = (
  inputNames: string[] = [],
  detail = "Input field values"
): RuntimeVariable => ({
  name: "inputs",
  kind: "record",
  properties: buildInputCompletions(inputNames),
  detail,
  documentation: "Values currently collected from interactive input fields.",
});

const primitiveVariable = (
  name: string,
  detail: string,
  documentation?: string
): RuntimeVariable => ({
  name,
  kind: "primitive",
  detail,
  documentation,
});

export const buildJsEditorRuntimeCompletionContext = ({
  runtimeContext,
  perVideoSchema,
  liveControlSchema,
  inputNames = [],
  isControlDeprecated = false,
}: BuildRuntimeContextOptions): JsEditorRuntimeCompletionContext => {
  const dataSchema = parseJsonSchemaForEditor(perVideoSchema);
  const controlSchema = parseJsonSchemaForEditor(liveControlSchema);
  const variables: RuntimeVariable[] = [];

  switch (runtimeContext) {
    case "dynamicValueExpression":
      variables.push(dataVariable(dataSchema), inputVariable(inputNames));
      if (!isControlDeprecated) {
        variables.push(
          schemaVariable(
            "control",
            controlSchema,
            "Live control data",
            "Live control data derived from the project control schema."
          )
        );
      }
      break;
    case "playerEvent":
      variables.push(dataVariable(dataSchema));
      if (!isControlDeprecated) {
        variables.push(
          schemaVariable(
            "liveData",
            controlSchema,
            "Live control data",
            "Second argument passed to player event handlers."
          )
        );
      }
      variables.push(
        primitiveVariable(
          "eventData",
          "Player event payload",
          "Payload emitted by the selected player event."
        )
      );
      break;
    case "interactiveExpression":
      variables.push(dataVariable(dataSchema));
      if (!isControlDeprecated) {
        variables.push(
          schemaVariable(
            "liveData",
            controlSchema,
            "Live control data",
            "Second argument passed to interactive JavaScript handlers."
          )
        );
      }
      variables.push(
        { name: "event", kind: "event", detail: "Event" },
        { name: "element", kind: "element", detail: "SVGElement" },
        { name: "player", kind: "player", detail: "Blings Player" }
      );
      break;
    case "interactiveConcat":
      variables.push(dataVariable(dataSchema), inputVariable(inputNames));
      if (!isControlDeprecated) {
        variables.push(
          schemaVariable(
            "liveControl",
            controlSchema,
            "Live control data",
            "Live control data passed to advanced concat functions."
          )
        );
      }
      variables.push(
        { name: "event", kind: "event", detail: "Event" },
        { name: "element", kind: "element", detail: "SVGElement" },
        { name: "player", kind: "player", detail: "Blings Player" },
        { name: "jumpTypes", kind: "jumpTypes", detail: "Concat jump types" }
      );
      break;
    case "interactiveForm":
      variables.push(
        inputVariable(inputNames, "Submitted input field values"),
        primitiveVariable("sessionId", "string | undefined"),
        primitiveVariable("userId", "string"),
        dataVariable(dataSchema)
      );
      if (!isControlDeprecated) {
        variables.push(
          schemaVariable(
            "liveControlData",
            controlSchema,
            "Live control data",
            "Live control data passed as the fifth argument."
          )
        );
      }
      break;
    case "embedElement":
      variables.push(dataVariable(dataSchema));
      if (!isControlDeprecated) {
        variables.push(
          schemaVariable(
            "liveControlData",
            controlSchema,
            "Live control data",
            "Live control data passed to the embed render function."
          )
        );
      }
      variables.push(
        { name: "player", kind: "player", detail: "Blings Player | undefined" },
        { name: "layerNode", kind: "element", detail: "SVGElement" },
        {
          name: "foreignObject",
          kind: "foreignObject",
          detail: "SVGForeignObjectElement",
        }
      );
      break;
    default:
      variables.push(dataVariable(dataSchema));
      if (!isControlDeprecated) {
        variables.push(
          schemaVariable(
            "control",
            controlSchema,
            "Live control data",
            "Live control data derived from the project control schema."
          )
        );
      }
      break;
  }

  return { runtimeContext, variables };
};

export const getMemberExpressionInfo = (
  lineUntilPosition: string
): MemberExpressionInfo | undefined => {
  const normalizedLine = lineUntilPosition.replace(/\?\./g, ".");
  const match = /([A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*\.?)$/.exec(
    normalizedLine
  );
  if (!match) return undefined;

  const expression = match[1];
  const hasMemberAccess = expression.includes(".");
  const endsWithDot = expression.endsWith(".");
  const segments = expression.split(".");
  const root = segments[0];
  const prefix = endsWithDot ? "" : segments[segments.length - 1];
  const path = segments.slice(1, -1);

  return { root, path, prefix, hasMemberAccess };
};

export const getJsEditorRuntimeCompletions = (
  context: JsEditorRuntimeCompletionContext,
  lineUntilPosition: string
): JsEditorRuntimeCompletion[] => {
  const memberExpression = getMemberExpressionInfo(lineUntilPosition);
  if (!memberExpression) return [];

  if (!memberExpression.hasMemberAccess) {
    return context.variables
      .filter((variable) => variable.name.startsWith(memberExpression.prefix))
      .map((variable) => ({
        label: variable.name,
        insertText: variable.name,
        detail: variable.detail,
        documentation: variable.documentation,
        kind: "variable",
      }));
  }

  const variable = context.variables.find(
    (candidate) => candidate.name === memberExpression.root
  );
  if (!variable) return [];

  return getVariableMemberCompletions(
    variable,
    memberExpression.path
  ).filter((completion) =>
    completion.label.startsWith(memberExpression.prefix)
  );
};

const getVariableMemberCompletions = (
  variable: RuntimeVariable,
  path: string[]
): JsEditorRuntimeCompletion[] => {
  switch (variable.kind) {
    case "schema":
      return getSchemaPropertyCompletions(variable.schema, path);
    case "record":
      return path.length ? [] : variable.properties || [];
    case "player":
      return path.length ? [] : PLAYER_COMPLETIONS;
    case "event":
      return path.length ? [] : EVENT_COMPLETIONS;
    case "element":
      return path.length ? [] : ELEMENT_COMPLETIONS;
    case "foreignObject":
      return path.length ? [] : FOREIGN_OBJECT_COMPLETIONS;
    case "jumpTypes":
      return path.length ? [] : JUMP_TYPE_COMPLETIONS;
    default:
      return [];
  }
};

const getSchemaPropertyCompletions = (
  schema: JSONSchema7 | undefined,
  path: string[]
): JsEditorRuntimeCompletion[] => {
  const resolvedSchema = resolveSchemaPath(schema, path);
  if (!resolvedSchema) return [];

  const example =
    Array.isArray(resolvedSchema.examples) && resolvedSchema.examples.length
      ? resolvedSchema.examples[0]
      : resolvedSchema.default;

  const properties = collectSchemaProperties(resolvedSchema, example);

  return properties.map(([key, propertySchema, propertyExample]) => ({
    label: key,
    insertText: getPropertyInsertText(key),
    detail: getSchemaDetail(propertySchema, propertyExample),
    documentation: getSchemaDocumentation(propertySchema, propertyExample),
    kind: "property" as const,
    replacePreviousDot: !isValidIdentifier(key),
  }));
};

const resolveSchemaPath = (
  schema: JSONSchema7 | undefined,
  path: string[]
): JSONSchema7 | undefined => {
  let current = schema;
  for (const segment of path) {
    if (!current) return undefined;

    const properties = current.properties || {};
    const nextSchema = properties[segment];
    current = isJsonSchemaObject(nextSchema) ? nextSchema : undefined;
  }

  return current;
};

const collectSchemaProperties = (
  schema: JSONSchema7,
  example: unknown
): Array<[string, JSONSchema7 | undefined, unknown]> => {
  const propertyEntries = Object.entries(schema.properties || {}).map(
    ([key, value]) =>
      [
        key,
        isJsonSchemaObject(value) ? value : undefined,
        isObject(example) ? example[key] : undefined,
      ] as [string, JSONSchema7 | undefined, unknown]
  );

  if (!propertyEntries.length && isObject(example)) {
    return Object.entries(example).map(
      ([key, value]) =>
        [key, undefined, value] as [string, JSONSchema7 | undefined, unknown]
    );
  }

  return propertyEntries;
};

const getSchemaDetail = (
  schema: JSONSchema7 | undefined,
  example: unknown
): string => {
  if (!schema) return getValueType(example);
  const type = Array.isArray(schema.type)
    ? schema.type.join(" | ")
    : schema.type || getValueType(example);

  if (schema.format) return `${type} (${schema.format})`;
  if (schema.title) return `${type} - ${schema.title}`;
  return type;
};

const getSchemaDocumentation = (
  schema: JSONSchema7 | undefined,
  example: unknown
): string | undefined => {
  const details: string[] = [];
  if (schema?.description) details.push(schema.description);
  if (typeof schema?.default !== "undefined") {
    details.push(`Default: ${stringifyForDocs(schema.default)}`);
  }
  if (typeof example !== "undefined") {
    details.push(`Example: ${stringifyForDocs(example)}`);
  }
  return details.join("\n\n") || undefined;
};

const getValueType = (value: unknown): string => {
  if (Array.isArray(value)) return "array";
  if (value === null) return "null";
  if (typeof value === "undefined") return "unknown";
  return typeof value;
};

const stringifyForDocs = (value: unknown): string => {
  if (typeof value === "string") return JSON.stringify(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const isValidIdentifier = (value: string): boolean =>
  /^[A-Za-z_$][\w$]*$/.test(value);

const getPropertyInsertText = (propertyName: string): string =>
  isValidIdentifier(propertyName)
    ? propertyName
    : `["${propertyName.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`;
